import React from 'react';
import { View, StyleSheet, ActivityIndicator, StyleProp, ViewStyle } from 'react-native';
import { Text } from '@/components/common/text';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { SectionHeader } from '../SectionHeader';
import { GlassCard } from '../GlassCard';
import { AutopayCard } from './AutopayCard';

const PayIcon = RESIDENTS_SVG_COMPONENTS.PAY;

export interface AutopayLease {
  leaseId: number;
  autopayId?: number;
  displayName: string;
  initials: string;
  photoUrl?: string;
  propertyName: string;
  autopayEnabled: boolean;
  amount?: string;
  paymentMethodLabel?: string;
  startDate?: string;
  endDate?: string;
  processedDay?: string;
  isSetupDisabled?: boolean;
}

interface AutopaySectionProps {
  leases: AutopayLease[];
  onSetup: (leaseId: number) => void;
  onEdit: (leaseId: number) => void;
  onRemove: (leaseId: number, autopayId: number) => void;
  /** The lease whose autopay removal is in flight; its card shows the pending state. */
  removingLeaseId?: number | null;
  /** The lease whose autopay setup is in flight; its card shows the pending state. */
  settingUpLeaseId?: number | null;
  isLoading?: boolean;
  emptyStateIcon?: React.ReactNode;
  emptyStateTitle?: string;
  emptyStateMessage?: string;
  sectionTitle?: string;
  style?: StyleProp<ViewStyle>;
}

export const AutopaySection: React.FC<AutopaySectionProps> = React.memo(({
  leases,
  onSetup,
  onEdit,
  onRemove,
  removingLeaseId = null,
  settingUpLeaseId = null,
  isLoading = false,
  emptyStateIcon,
  emptyStateTitle = 'No autopay configured',
  emptyStateMessage = 'Set up autopay to automate your rent payments and never miss a due date.',
  sectionTitle = 'Autopay',
  style,
}) => {
  const { colors, semantic } = useThemeColors();

  if (isLoading) {
    return (
      <View style={[styles.loadingContainer, style]}>
        <ActivityIndicator color={semantic.interactive.primary} />
      </View>
    );
  }

  if (leases.length === 0) {
    return (
      <View style={style}>
        <SectionHeader title={sectionTitle} />
        <GlassCard style={styles.emptyCard}>
          <View style={styles.emptyIconOuter}>
            <View style={styles.emptyIconGlow} />
            <View style={styles.emptyIconContainer}>
              {emptyStateIcon || (PayIcon && (
                <PayIcon width={24} height={24} color={colors.accent.green} />
              ))}
            </View>
          </View>
          <Text style={[styles.emptyTitle, { color: colors.text.primary }]}>{emptyStateTitle}</Text>
          <Text style={[styles.emptyMessage, { color: colors.text.muted }]}>{emptyStateMessage}</Text>
        </GlassCard>
      </View>
    );
  }

  return (
    <View style={style}>
      <SectionHeader title={sectionTitle} />
      <View style={styles.leasesList}>
        {leases.map((lease) => (
          <AutopayCard
            key={lease.leaseId}
            displayName={lease.displayName}
            initials={lease.initials}
            photoUrl={lease.photoUrl}
            propertyName={lease.propertyName}
            autopayEnabled={lease.autopayEnabled}
            amount={lease.amount}
            paymentMethodLabel={lease.paymentMethodLabel}
            startDate={lease.startDate}
            endDate={lease.endDate}
            processedDay={lease.processedDay}
            isSetupDisabled={lease.isSetupDisabled}
            isRemoving={lease.leaseId === removingLeaseId}
            isSettingUp={lease.leaseId === settingUpLeaseId}
            onSetup={() => onSetup(lease.leaseId)}
            onEdit={() => onEdit(lease.leaseId)}
            onRemove={lease.autopayId
              ? () => onRemove(lease.leaseId, lease.autopayId!)
              : undefined
            }
          />
        ))}
      </View>
    </View>
  );
});

AutopaySection.displayName = 'AutopaySection';

const styles = StyleSheet.create({
  loadingContainer: {
    paddingVertical: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  leasesList: {
    gap: 12,
  },
  emptyCard: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 24,
  },
  emptyIconOuter: {
    width: 64,
    height: 64,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  emptyIconGlow: {
    position: 'absolute',
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(102,206,102,0.06)',
  },
  emptyIconContainer: {
    width: 52,
    height: 52,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyMessage: {
    ...REDESIGN_TYPOGRAPHY.body,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 280,
  },
});
